import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type UIEvent,
} from 'react';
import { Box, type BoxProps } from '@chakra-ui/react';
import { OVERLAY_SCROLL_CSS } from '../layout/layoutConstants';

const THUMB_MIN_PX = 28;
const THUMB_HIT_PX = 10;
/** Pointer within this distance of the trailing edge keeps the thumb shown. */
const EDGE_PX = 16;
const HIDE_AFTER_MS = 700;

interface ThumbGeometry {
  size: number;
  offset: number;
}

interface ThumbDrag {
  pointerId: number;
  startY: number;
  startScroll: number;
  thumbSize: number;
}

/**
 * Thumb size and offset inside the visible pane. `null` when the content fits.
 * `offset` is the distance from the top of the viewport, not the scrolled content.
 */
export function overlayThumbGeometry(
  scrollSize: number,
  clientSize: number,
  scrollOffset: number,
): ThumbGeometry | null {
  if (clientSize <= 0 || scrollSize <= 0) return null;
  const overflow = scrollSize - clientSize;
  if (overflow <= 1) return null;
  const size = Math.min(
    clientSize,
    Math.max(THUMB_MIN_PX, (clientSize / scrollSize) * clientSize),
  );
  const track = Math.max(0, clientSize - size);
  const offset = track === 0 ? 0 : (Math.min(Math.max(scrollOffset, 0), overflow) / overflow) * track;
  return { size, offset };
}

const OVERLAY_THUMB_CSS = {
  display: 'flex',
  justifyContent: 'center',
  touchAction: 'none',
  cursor: 'grab',
  pointerEvents: 'none',
  opacity: 0,
  transition: 'opacity 160ms ease',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  '@media (pointer: coarse)': { display: 'none' },
  '&[data-overflow="false"]': { display: 'none' },
  '&[data-visible="true"]': { opacity: 0.8, pointerEvents: 'auto' },
  '&[data-hot="true"]': { opacity: 1 },
  '&[data-dragging="true"]': { opacity: 1, cursor: 'grabbing' },
  '&::before': {
    content: '""',
    width: '6px',
    height: '100%',
    borderRadius: '999px',
    background: 'var(--chakra-colors-text-muted, #9C8FBF)',
  },
  '&[data-hot="true"]::before': {
    background: 'var(--chakra-colors-text, #f2edff)',
  },
  '&[data-dragging="true"]::before': {
    background: 'var(--chakra-colors-accent, #d2b45a)',
  },
} as const;

/** Scroll offset after dragging the thumb by `delta` pixels along the track. */
export function scrollOffsetForThumbDrag(
  startScroll: number,
  delta: number,
  scrollSize: number,
  clientSize: number,
  thumbSize: number,
): number {
  const overflow = scrollSize - clientSize;
  const track = clientSize - thumbSize;
  if (overflow <= 0 || track <= 0) return startScroll;
  const next = startScroll + (delta / track) * overflow;
  return Math.min(overflow, Math.max(0, next));
}

/**
 * Scrollport with an overlay thumb: no track and no reserved gutter.
 * The thumb fades in while scrolling and while the pointer is on the trailing
 * edge, and it can be dragged. Wheel, trackpad, and keyboard scrolling stay native.
 */
export const OverlayScroll = forwardRef<HTMLDivElement, BoxProps>(function OverlayScroll(
  {
    children,
    css,
    position,
    minH,
    onScroll,
    onPointerMove,
    onPointerLeave,
    overflow: _overflow,
    overflowX: _overflowX,
    overflowY: _overflowY,
    ...rest
  },
  forwardedRef,
) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef(false);
  const edgeRef = useRef(false);
  const hotRef = useRef(false);
  const dragRef = useRef<ThumbDrag | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const applyThumbRef = useRef<() => void>(() => {});

  const setRoot = useCallback(
    (node: HTMLDivElement | null) => {
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  const clearHide = () => {
    if (hideTimerRef.current == null) return;
    window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  };

  const isPinned = () => edgeRef.current || hotRef.current || dragRef.current !== null;

  applyThumbRef.current = () => {
    const el = scrollerRef.current;
    const thumb = thumbRef.current;
    if (!el || !thumb) return;
    const geo = overlayThumbGeometry(el.scrollHeight, el.clientHeight, el.scrollTop);
    const overflow = geo !== null;
    thumb.dataset.overflow = overflow ? 'true' : 'false';
    thumb.dataset.visible = overflow && visibleRef.current ? 'true' : 'false';
    thumb.dataset.hot = hotRef.current ? 'true' : 'false';
    thumb.dataset.dragging = dragRef.current ? 'true' : 'false';
    if (!geo) return;
    thumb.style.height = `${geo.size}px`;
    thumb.style.top = `${geo.offset}px`;
  };

  const applyThumb = () => applyThumbRef.current();

  const scheduleHide = () => {
    clearHide();
    if (isPinned()) return;
    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = null;
      if (isPinned()) return;
      visibleRef.current = false;
      applyThumb();
    }, HIDE_AFTER_MS);
  };

  const reveal = () => {
    const el = scrollerRef.current;
    if (!el || el.scrollHeight - el.clientHeight <= 1) return;
    visibleRef.current = true;
    applyThumb();
    scheduleHide();
  };

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const paint = () => applyThumbRef.current();
    if (typeof ResizeObserver === 'undefined') {
      paint();
      return () => clearHide();
    }
    let observer = new ResizeObserver(paint);
    const observe = () => {
      observer.disconnect();
      observer = new ResizeObserver(paint);
      observer.observe(el);
      for (const child of el.children) {
        if (child instanceof HTMLElement) observer.observe(child);
      }
    };
    observe();
    const mutations = new MutationObserver(observe);
    mutations.observe(el, { childList: true });
    paint();
    return () => {
      observer.disconnect();
      mutations.disconnect();
      clearHide();
    };
  }, []);

  useLayoutEffect(() => {
    applyThumb();
  });

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    onScroll?.(event);
    applyThumb();
    reveal();
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    onPointerMove?.(event);
    const rect = event.currentTarget.getBoundingClientRect();
    const edge = rect.right - event.clientX <= EDGE_PX && event.clientX >= rect.left;
    if (edge === edgeRef.current) return;
    edgeRef.current = edge;
    if (edge) reveal();
    else scheduleHide();
  };

  const handlePointerLeave = (event: ReactPointerEvent<HTMLDivElement>) => {
    onPointerLeave?.(event);
    edgeRef.current = false;
    if (!isPinned()) scheduleHide();
  };

  const handleThumbPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const el = scrollerRef.current;
    const thumb = thumbRef.current;
    if (!el || !thumb) return;
    const geo = overlayThumbGeometry(el.scrollHeight, el.clientHeight, el.scrollTop);
    if (!geo) return;
    event.preventDefault();
    event.stopPropagation();
    thumb.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startScroll: el.scrollTop,
      thumbSize: geo.size,
    };
    visibleRef.current = true;
    clearHide();
    applyThumb();
  };

  const handleThumbPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const el = scrollerRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !el) return;
    el.scrollTop = scrollOffsetForThumbDrag(
      drag.startScroll,
      event.clientY - drag.startY,
      el.scrollHeight,
      el.clientHeight,
      drag.thumbSize,
    );
  };

  const endThumbDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (thumbRef.current?.hasPointerCapture(event.pointerId)) {
      thumbRef.current.releasePointerCapture(event.pointerId);
    }
    applyThumb();
    scheduleHide();
  };

  return (
    <Box
      ref={setRoot}
      {...rest}
      position={position ?? 'relative'}
      minH={minH ?? 0}
      overflow="hidden"
      css={css}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      <Box
        ref={scrollerRef}
        h="100%"
        maxH="inherit"
        minH={0}
        overflowX="hidden"
        overflowY="auto"
        overscrollBehavior="contain"
        onScroll={handleScroll}
        css={{ ...OVERLAY_SCROLL_CSS }}
      >
        {children}
      </Box>
      <Box
        ref={thumbRef}
        aria-hidden
        data-overlay-thumb=""
        data-overflow="false"
        data-visible="false"
        data-hot="false"
        data-dragging="false"
        position="absolute"
        top={0}
        right="2px"
        zIndex={2}
        w={`${THUMB_HIT_PX}px`}
        borderRadius="full"
        onPointerEnter={() => {
          hotRef.current = true;
          reveal();
        }}
        onPointerLeave={() => {
          hotRef.current = false;
          applyThumb();
          scheduleHide();
        }}
        onPointerDown={handleThumbPointerDown}
        onPointerMove={handleThumbPointerMove}
        onPointerUp={endThumbDrag}
        onPointerCancel={endThumbDrag}
        css={OVERLAY_THUMB_CSS}
      />
    </Box>
  );
});

function documentScroller(): HTMLElement {
  return document.scrollingElement instanceof HTMLElement
    ? document.scrollingElement
    : document.documentElement;
}

function viewportCanScroll(el: HTMLElement): boolean {
  const root = getComputedStyle(document.documentElement).overflowY;
  const body = getComputedStyle(document.body).overflowY;
  if (root === 'hidden' || root === 'clip' || body === 'hidden' || body === 'clip') return false;
  return el.scrollHeight - el.clientHeight > 1;
}

/**
 * Overlay thumb for the window scrollbar. The native viewport bar is hidden in
 * `scrollbar.css`, so every page gets the same fade-in thumb as {@link OverlayScroll}.
 */
export function WindowOverlayScroll() {
  const thumbRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef(false);
  const edgeRef = useRef(false);
  const hotRef = useRef(false);
  const dragRef = useRef<ThumbDrag | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const applyThumbRef = useRef<() => void>(() => {});

  const clearHide = () => {
    if (hideTimerRef.current == null) return;
    window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  };

  const isPinned = () => edgeRef.current || hotRef.current || dragRef.current !== null;

  applyThumbRef.current = () => {
    const thumb = thumbRef.current;
    if (!thumb) return;
    const el = documentScroller();
    const geo = viewportCanScroll(el)
      ? overlayThumbGeometry(el.scrollHeight, el.clientHeight, el.scrollTop || window.scrollY)
      : null;
    thumb.dataset.overflow = geo ? 'true' : 'false';
    thumb.dataset.visible = geo && visibleRef.current ? 'true' : 'false';
    thumb.dataset.hot = hotRef.current ? 'true' : 'false';
    thumb.dataset.dragging = dragRef.current ? 'true' : 'false';
    if (!geo) return;
    thumb.style.height = `${geo.size}px`;
    thumb.style.top = `${geo.offset}px`;
  };

  const applyThumb = () => applyThumbRef.current();

  const scheduleHide = () => {
    clearHide();
    if (isPinned()) return;
    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = null;
      if (isPinned()) return;
      visibleRef.current = false;
      applyThumb();
    }, HIDE_AFTER_MS);
  };

  const reveal = () => {
    const el = documentScroller();
    if (!viewportCanScroll(el)) return;
    visibleRef.current = true;
    applyThumb();
    scheduleHide();
  };

  useLayoutEffect(() => {
    const paint = () => applyThumbRef.current();
    if (typeof ResizeObserver === 'undefined') {
      paint();
      return () => {
        if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
      };
    }
    const onScroll = () => {
      paint();
      const el = documentScroller();
      if (!viewportCanScroll(el)) return;
      visibleRef.current = true;
      paint();
      if (edgeRef.current || hotRef.current || dragRef.current) {
        if (hideTimerRef.current != null) {
          window.clearTimeout(hideTimerRef.current);
          hideTimerRef.current = null;
        }
        return;
      }
      if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = window.setTimeout(() => {
        hideTimerRef.current = null;
        if (edgeRef.current || hotRef.current || dragRef.current) return;
        visibleRef.current = false;
        paint();
      }, HIDE_AFTER_MS);
    };
    const onPointerMove = (event: PointerEvent) => {
      const edge = window.innerWidth - event.clientX <= EDGE_PX && event.clientX >= 0;
      if (edge === edgeRef.current) return;
      edgeRef.current = edge;
      if (edge) {
        const el = documentScroller();
        if (!viewportCanScroll(el)) return;
        visibleRef.current = true;
        paint();
        if (hideTimerRef.current != null) {
          window.clearTimeout(hideTimerRef.current);
          hideTimerRef.current = null;
        }
      } else if (!hotRef.current && !dragRef.current) {
        if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = window.setTimeout(() => {
          hideTimerRef.current = null;
          if (edgeRef.current || hotRef.current || dragRef.current) return;
          visibleRef.current = false;
          paint();
        }, HIDE_AFTER_MS);
      }
    };
    const onPointerLeave = () => {
      edgeRef.current = false;
      if (hotRef.current || dragRef.current) return;
      if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = window.setTimeout(() => {
        hideTimerRef.current = null;
        if (edgeRef.current || hotRef.current || dragRef.current) return;
        visibleRef.current = false;
        paint();
      }, HIDE_AFTER_MS);
    };

    const observer = new ResizeObserver(paint);
    observer.observe(document.documentElement);
    observer.observe(document.body);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', paint);
    window.addEventListener('pointermove', onPointerMove);
    document.documentElement.addEventListener('pointerleave', onPointerLeave);
    paint();
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', paint);
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('pointerleave', onPointerLeave);
      if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    applyThumb();
  });

  const handleThumbPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const el = documentScroller();
    const thumb = thumbRef.current;
    if (!thumb || !viewportCanScroll(el)) return;
    const geo = overlayThumbGeometry(el.scrollHeight, el.clientHeight, el.scrollTop || window.scrollY);
    if (!geo) return;
    event.preventDefault();
    event.stopPropagation();
    thumb.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startScroll: el.scrollTop || window.scrollY,
      thumbSize: geo.size,
    };
    visibleRef.current = true;
    clearHide();
    applyThumb();
  };

  const handleThumbPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const el = documentScroller();
    const next = scrollOffsetForThumbDrag(
      drag.startScroll,
      event.clientY - drag.startY,
      el.scrollHeight,
      el.clientHeight,
      drag.thumbSize,
    );
    el.scrollTop = next;
    window.scrollTo({ top: next });
  };

  const endThumbDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (thumbRef.current?.hasPointerCapture(event.pointerId)) {
      thumbRef.current.releasePointerCapture(event.pointerId);
    }
    applyThumb();
    scheduleHide();
  };

  return (
    <Box
      ref={thumbRef}
      aria-hidden
      data-overlay-thumb=""
      data-window-overlay-thumb=""
      data-overflow="false"
      data-visible="false"
      data-hot="false"
      data-dragging="false"
      position="fixed"
      top={0}
      right="2px"
      zIndex={80}
      w={`${THUMB_HIT_PX}px`}
      borderRadius="full"
      onPointerEnter={() => {
        hotRef.current = true;
        reveal();
      }}
      onPointerLeave={() => {
        hotRef.current = false;
        applyThumb();
        scheduleHide();
      }}
      onPointerDown={handleThumbPointerDown}
      onPointerMove={handleThumbPointerMove}
      onPointerUp={endThumbDrag}
      onPointerCancel={endThumbDrag}
      css={OVERLAY_THUMB_CSS}
    />
  );
}
